// core/services/firebase/socialService.ts

import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  query, 
  where, 
  getDocs,
  updateDoc 
} from 'firebase/firestore';
import { db } from './config';
import { SocialProfile } from '../../types/social';
import { searchService } from '../algolia/searchService';

export const socialService = {
  /**
   * Check if a handle is available
   */
  async isHandleAvailable(handle: string): Promise<boolean> {
    const q = query(collection(db, 'profiles'), where('handle', '==', handle.toLowerCase()));
    const snapshot = await getDocs(q);
    return snapshot.empty;
  },

  /**
   * Create a new Social Profile
   * Uses the userId as the document ID to comply with standard security rules
   */
  async createProfile(profile: Omit<SocialProfile, 'createdAt' | 'updatedAt'>): Promise<void> {
    const handle = profile.handle.toLowerCase();
    
    // Safety check for handle uniqueness
    const available = await this.isHandleAvailable(handle);
    if (!available) {
      throw new Error('Handle already taken');
    }

    if (!profile.userId) {
      throw new Error("User ID is required to create a profile");
    }

    // Use userId as document ID
    const profileRef = doc(db, 'profiles', profile.userId); 
    
    // Safety check: ensure user doesn't already have a profile (overwrite protection not strictly needed if rules allow update, but good for logic)
    const exists = await getDoc(profileRef);
    if (exists.exists()) {
      // If profile exists, we might want to update it or throw? 
      // For createProfile, we act as upsert usually, but let's be safe.
      // If we are here, it means we entered "create" flow.
      console.warn("Profile already exists for this user, overwriting...");
    }

    const newProfile = {
      ...profile,
      id: profile.userId, // ID matches document ID
      handle: handle,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await setDoc(profileRef, newProfile);
    
    // Index in Algolia
    await searchService.indexProfile(newProfile as any);
  },

  /**
   * Get Profile by Handle
   * Now requires a query since ID != handle
   */
  async getProfileByHandle(handle: string): Promise<SocialProfile | null> {
    const q = query(collection(db, 'profiles'), where('handle', '==', handle.toLowerCase()));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].data() as SocialProfile;
  },

  /**
   * Get Profile by User ID (Auth)
   * Kept as query for backward compatibility (in case some old profiles have ID != userId)
   * Optimization: Could check doc(db, 'profiles', userId) first
   */
  async getProfileByUserId(userId: string): Promise<SocialProfile | null> {
    // Optimistic check directly by ID (fastest)
    const docRef = doc(db, 'profiles', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as SocialProfile;
    }

    // Fallback: Query (backward compatibility for ID=Handle profiles)
    const q = query(collection(db, 'profiles'), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].data() as SocialProfile;
  },

  /**
   * Update Profile
   * Handle is passed, but we need to find the doc first.
   * Efficiently we should pass userId, but respecting interface...
   */
  async updateProfile(handle: string, updates: Partial<SocialProfile>): Promise<void> {
    // 1. Try to find by handle (query)
    const profile = await this.getProfileByHandle(handle);
    if (!profile) throw new Error("Profile not found");

    // 2. Update found document by its ID (which might be handle or userId)
    // profile.id usually holds the document ID if we stored it correctly.
    // If not, we can rely on getProfileByHandle returning the full object which we hope has 'id'.
    // NOTE: In createProfile above we set 'id: profile.userId'. 
    // Legacy profiles might have 'id: handle'.
    
    // If profile.id is missing (shouldn't be), fallback to query snapshot lookup
    let docId = profile.id;
    if (!docId) {
       // Re-query to get ref (inefficient but safe)
       const q = query(collection(db, 'profiles'), where('handle', '==', handle.toLowerCase()));
       const snapshot = await getDocs(q);
       if (!snapshot.empty) docId = snapshot.docs[0].id;
    }

    if (!docId) throw new Error("Could not determine profile ID");

    const docRef = doc(db, 'profiles', docId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  }
};
